import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  computeProgression,
  ligaForPoints,
  MILESTONES,
  sealSeason,
  seasonRange,
  type ProgressionState,
} from '@selflevel/domain';
import { db } from '../store/db';
import { useProgression, useSettings, useToday } from '../store/hooks';
import { collectProgressionEvents, pendingSeasonsToSeal } from '../store/selectors';
import { sealSeasonRow, updateSettings } from '../store/mutations';
import { bootstrapPendiente, NIVEL_VISTO, progresionAhora } from '../store/progresion-compartida';
import { Divisa } from '../ui/iconos';
import { Insignia, Sello, num } from '../ui/primitivas';
import { indiceDeTramo, tramoAlcanzado } from './camino';
import { useFocusRunning } from './foco-estado';
import { celebracionDeNivel } from './subida-nivel';

/**
 * LOS CUATRO ACONTECIMIENTOS, con cuatro piezas distintas (paso 7B).
 *
 *   nivel      1.600 ms · AVISA     · píldora al pie, SE CIERRA SOLA, no bloquea
 *   hito       2.600 ms · REBOTA    · panel con el sello acuñándose y la barra
 *   tramo      2.200 ms · SE ASIENTA· panel de acento a sangre, sin rebote
 *   temporada  1.600 ms · SE ESTAMPA· panel teñido por las áreas
 *
 * El «nivel» es nuevo y es **deliberadamente la pieza menor**. Sube de nivel
 * general quien mueve la media de sus áreas, y eso pasa a menudo: un modal cada
 * vez sería cobrarle un peaje a alguien por progresar. Avisa y se va.
 * El «hito» es el opuesto: es el momento, y por eso es el único con barra que se
 * rellena de un extremo al otro del tramo y con el sello acuñándose. La
 * diferencia entre los dos tiene que notarse sin leer nada.
 *
 * Si coinciden, el orden es nivel → hito → tramo → temporada con 400 ms. Y
 * **durante una sesión de foco todo se encola**: interrumpir a alguien que está
 * concentrado para enseñarle una medalla es exactamente lo que esta app no hace.
 *
 * Los 800 ms de más del tramo frente al hito no son decoración: son el segundo
 * movimiento, en el que la divisa recorre la tira hasta su casilla nueva. El
 * usuario ve que ha cambiado de sitio, no solo que ha ganado algo.
 */

type Evento =
  | {
      kind: 'nivel';
      nivel: number;
      desde: number;
      /** Un arco por área, con su tinte y su progreso: el mismo dial de la
       *  cabecera de Progreso. Se captura al disparar el evento y no se lee en
       *  el render, para que la pieza enseñe el estado del momento en que
       *  subiste y no el de tres segundos después. */
      arcos: readonly { token: string; progress: number }[];
    }
  | {
      kind: 'hito';
      key: string;
      nombre: string;
      recompensa: string;
      n: number;
      /** Los dos extremos de la barra: el hito anterior y este. */
      desdePuntos: number;
      hastaPuntos: number;
    }
  | { kind: 'tramo'; nombre: string; clave: string; desde: number }
  | {
      kind: 'temporada';
      etiqueta: string;
      liga: string;
      fill: number;
      puntos: number;
      hitos: number;
    };

const DURACION = { nivel: 2400, hito: 2600, tramo: 2200, temporada: 1600 } as const;

/** Dónde se guarda el último nivel general que se le ha enseñado a esta persona.
 *  Va en `meta` y no en `settings` **a propósito**: `settings` se sincroniza, y
 *  una celebración es un momento de un dispositivo, no un dato de la cuenta. Si
 *  viajara, subir de nivel en el móvil te lo volvería a celebrar el portátil tres
 *  días después, fuera de contexto. Y de paso se ahorra una columna y su
 *  migración en el servidor para algo que el servidor no necesita saber.
 *  La clave, `NIVEL_VISTO`, vive en `progresion-compartida`: también hace de suelo. */

/** Si ya se tomó la línea base tras la última descarga inicial (ver
 *  `detectarNovedades`). */
const LINEA_BASE = 'novedadesLineaBase';

/**
 * LO NUEVO DESDE LA ÚLTIMA VEZ: hitos, subida de nivel y cruce de tramo.
 *
 * Todo lo «ya visto» se lee de la BASE —los hitos sellados, el nivel anotado,
 * el último tramo— y no de las props. Es lo que hace que llamarla dos veces
 * seguidas no celebre nada dos veces: la segunda ve lo que escribió la primera.
 * Con `settings.lastSeenMilestone` sacado del render, una segunda vuelta que
 * llegara antes del siguiente render leería el tramo viejo y lo volvería a
 * anunciar.
 */
async function detectarNovedades(recibida: ProgressionState, today: string): Promise<Evento[]> {
  // Con la descarga inicial a medias no se mira nada: lo que «aparece» no es
  // progreso, es el historial llegando. Y se olvida la línea base, para que la
  // primera vuelta tras CADA descarga (vincular, re-bootstrap) se calle.
  if (await bootstrapPendiente()) {
    await db.meta.delete(LINEA_BASE);
    return [];
  }

  // LA LÍNEA BASE: la primera vuelta tras la descarga anota lo que hay como ya
  // visto sin celebrar nada. En un dispositivo nuevo, los hitos y el nivel de
  // hace meses no son noticia. Se recalcula aquí en vez de fiarse de la
  // recibida, que pudo calcularse con la descarga todavía a medias.
  const cuenta = (await db.meta.get('syncAccountId'))?.value;
  const silencio =
    typeof cuenta === 'string' &&
    cuenta.length > 0 &&
    (await db.meta.get(LINEA_BASE))?.value !== true;
  const progression = silencio ? await progresionAhora(today) : recibida;

  const eventos: Evento[] = [];

  for (const hito of progression.unlocked) {
    // Se lee y se escribe en la misma transacción: si el hito ya llegó por
    // pull, se respeta su fila —y su fecha— y no se celebra otra vez.
    const nuevo = await db.transaction('rw', db.milestones, async () => {
      if (await db.milestones.get(hito.key)) return false;
      await db.milestones.put({
        key: hito.key,
        n: hito.n,
        name: hito.name,
        unlockedAt: new Date().toISOString(),
        localDate: today,
        attested: true,
      });
      return true;
    });
    if (!nuevo || silencio) continue;
    eventos.push({
      kind: 'hito',
      key: hito.key,
      nombre: hito.name,
      recompensa: hito.unlocks,
      n: hito.n,
      // Los dos extremos de la barra que se rellena. El anterior es el hito
      // n−1 del catálogo, y para el primero es cero: el camino empieza en el
      // suelo, no en el hito menos uno.
      desdePuntos: MILESTONES[hito.n - 2]?.points ?? 0,
      hastaPuntos: hito.points,
    });
  }

  // SUBIDA DE NIVEL GENERAL. Se compara con lo guardado en `meta` y no con
  // el render anterior, por lo mismo que el tramo: así sobrevive a un
  // recargado y no se celebra dos veces.
  //
  // El caso que hay que blindar es la cuenta recién hecha: no hay nada
  // guardado y el nivel general ya vale 1, así que una comparación ingenua
  // le suelta un «¡nivel 1!» a alguien que no ha hecho nada. La primera vez
  // se anota y se calla.
  const visto = (await db.meta.get(NIVEL_VISTO))?.value;
  const { anotar, desde } = celebracionDeNivel(visto, progression.generalLevel, silencio);
  if (desde !== null) {
    const porArea = new Map(progression.dimensions.map((d) => [d.key, d]));
    // Las áreas se leen AQUÍ, de la base: el dial tiene que enseñar las
    // áreas del instante en que subiste, que es lo que se está celebrando.
    // (Esto nació como parche de un bucle —`useAreaMap` construía un `Map`
    // nuevo en cada render y tenerlo en las dependencias disparaba el
    // efecto sin fin—. Ese fallo ya está arreglado en el propio hook.)
    const areasAhora = await db.lifeAreas.toArray();
    eventos.push({
      kind: 'nivel',
      nivel: progression.generalLevel,
      desde,
      // De las ÁREAS, no de `dimensions`: una dimensión solo existe cuando
      // ha entrado algún punto, y el dial tiene que enseñar todas — un área
      // a cero es información, no un hueco. Mismo criterio que en Progreso.
      arcos: areasAhora
        .filter((a) => !a.archivedAt)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((a) => ({
          token: a.colorToken,
          progress: porArea.get(a.id)?.level.progress ?? 0,
        })),
    });
  }
  if (anotar !== null) await db.meta.put({ key: NIVEL_VISTO, value: anotar });

  // El cruce de tramo: se detecta comparando con lo último visto, no con el
  // estado anterior en memoria, para que sobreviva a un recargado. El tramo es
  // el ALCANZADO (no retrocede con los puntos) y lo anotado solo sube: si
  // bajara, recuperar los puntos volvería a celebrar un cruce ya celebrado.
  const tramo = tramoAlcanzado(progression);
  const ultimoVisto = (await db.settings.get('unica'))?.lastSeenMilestone ?? null;
  const indice = indiceDeTramo(tramo.key);
  const previo = indiceDeTramo(ultimoVisto);
  const conPuntos = progression.totalPoints > 0 || progression.unlocked.length > 0;
  if (indice > previo && conPuntos) {
    if (previo >= 0 && !silencio) {
      eventos.push({ kind: 'tramo', nombre: tramo.name, clave: tramo.key, desde: tramo.from });
    }
    await updateSettings({ lastSeenMilestone: tramo.key });
  }

  if (silencio) await db.meta.put({ key: LINEA_BASE, value: true });
  return eventos;
}

export function Acontecimientos(): ReactElement | null {
  const progression = useProgression();
  const settings = useSettings();
  const today = useToday();
  const focusRunning = useFocusRunning();
  const [cola, setCola] = useState<Evento[]>([]);
  const descargaPendiente = useLiveQuery(() => bootstrapPendiente(), []);

  // --- 1 · sellar las temporadas que ya deberían estarlo --------------------
  useEffect(() => {
    // Sellar con la descarga inicial a medias congelaría una temporada con
    // puntos de menos, y sellada no se corrige nunca.
    if (descargaPendiente !== false) return;
    let cancelled = false;
    void (async () => {
      const pendientes = await pendingSeasonsToSeal(today);
      if (pendientes.length === 0 || cancelled) return;

      const eventos = await collectProgressionEvents();
      const nuevas: Evento[] = [];

      for (const key of pendientes) {
        const sellada = sealSeason(eventos, key);
        // Se guarda Y se encola: sin el `season.seal` la temporada se quedaba
        // en este dispositivo y los demás la sellaban por su cuenta.
        const nueva = await sealSeasonRow({
          key: sellada.key,
          label: sellada.label,
          from: sellada.from,
          to: sellada.to,
          points: sellada.points,
          ligaKey: sellada.liga.key,
          ligaName: sellada.liga.name,
          milestones: sellada.milestones,
          sealedAt: new Date().toISOString(),
        });
        // Solo se anuncia la última: si alguien vuelve tras un año, no se le
        // encadenan cuatro paneles seguidos.
        if (nueva && key === pendientes.at(-1)) {
          nuevas.push({
            kind: 'temporada',
            etiqueta: sellada.label,
            liga: sellada.liga.name,
            fill: sellada.liga.fill,
            puntos: sellada.points,
            hitos: sellada.milestones,
          });
        }
      }

      if (!cancelled && nuevas.length > 0) setCola((q) => [...q, ...nuevas]);
    })();
    return () => {
      cancelled = true;
    };
  }, [today, descargaPendiente]);

  // --- 2 · hitos nuevos y cruce de tramo ------------------------------------
  //
  // UNA EJECUCIÓN A LA VEZ, y ninguna se descarta.
  //
  // Antes cada cambio de `progression` relanzaba el efecto y marcaba la
  // ejecución anterior como cancelada, y esa era la trampa: sellar un hito
  // escribe en `milestones`, eso hace que la progresión emita otra vez, y el
  // efecto se relanzaba A MITAD de la ejecución que acababa de sellarlo. La
  // vieja, cancelada, tiraba su evento; la nueva leía la tabla, veía el hito ya
  // sellado y no lo celebraba. **El hito se perdía**, y según lo rápido que
  // fuera el recálculo, a veces sí y a veces no.
  //
  // Ahora la que reclama un hito escribiéndolo es la que lo celebra, siempre.
  // Si llega otro cambio mientras corre, se anota y se vuelve a mirar al
  // terminar, con lo último que haya.
  const vigente = useRef({ progression, today });
  const enCurso = useRef(false);
  const otraVez = useRef(false);

  useEffect(() => {
    // Lo último, para la vuelta que esté en curso: si llega esto mientras corre
    // otra, esa otra lo leerá al repetir. Se escribe aquí y no durante el render,
    // que es donde React no deja tocar una ref.
    vigente.current = { progression, today };
    if (!progression) return;
    if (enCurso.current) {
      otraVez.current = true;
      return;
    }
    enCurso.current = true;

    void (async () => {
      try {
        do {
          otraVez.current = false;
          const actual = vigente.current;
          if (!actual.progression) break;
          const eventos = await detectarNovedades(actual.progression, actual.today);
          if (eventos.length > 0) {
            // nivel → hito → tramo → temporada. El orden importa: primero lo
            // pequeño. La subida de nivel abre —es la más leve, y además se
            // cierra sola— y el hito llega después como el momento de verdad.
            const orden = { nivel: 0, hito: 1, tramo: 2, temporada: 3 } as const;
            setCola((q) => [...q, ...eventos].sort((x, y) => orden[x.kind] - orden[y.kind]));
          }
        } while (otraVez.current);
      } finally {
        enCurso.current = false;
      }
    })();
  }, [progression, settings.lastSeenMilestone, today]);

  // --- 3 · reproducir la cola ------------------------------------------------
  //
  // Lo visible SE DERIVA de la cola en vez de vivir en un segundo estado que hay
  // que mantener sincronizado con ella. Un `useEffect` que copia un estado a
  // otro es siempre un render de más y, tarde o temprano, un desajuste.
  //
  // Durante foco, la cola no avanza: nada interrumpe una sesión.
  const enMarcha = focusRunning ? undefined : cola[0];
  if (!enMarcha) return null;

  const siguiente = () => setCola((q) => q.slice(1));

  // La subida de nivel no entra por el modal: es la pieza leve y va por su
  // propio camino, sin bloquear ni atrapar el foco. Comparte la cola con las
  // demás para que no se solapen y para que el foco la siga encolando.
  if (enMarcha.kind === 'nivel') {
    return <AvisoNivel key={`nivel:${enMarcha.nivel}`} evento={enMarcha} onClose={siguiente} />;
  }

  return (
    <Panel
      key={`${enMarcha.kind}:${'key' in enMarcha ? enMarcha.key : enMarcha.kind}`}
      evento={enMarcha}
      onClose={siguiente}
    />
  );
}

/**
 * LA SUBIDA DE NIVEL. La pieza leve de las dos.
 *
 * Es lo único de esta pantalla que **se cierra solo**, y es una excepción
 * deliberada a la regla de arriba —«un acontecimiento que se cierra solo se
 * siente como una notificación, no como un momento»—. Aquí eso es justo lo que
 * se busca: el nivel general es la media de tus áreas y sube a ratos, así que
 * pararle la app a alguien con un modal cada vez que sube sería cobrarle un
 * peaje por progresar. Avisa, se enseña y se va.
 *
 * No es un `role="dialog"`: no atrapa el foco ni bloquea nada. Es un aviso
 * cortés, y el lector de pantalla lo recibe por `role="status"`, que anuncia sin
 * robar el sitio donde estabas.
 */
function AvisoNivel({
  evento,
  onClose,
}: {
  evento: Evento & { kind: 'nivel' };
  onClose: () => void;
}): ReactElement {
  const [entrado, setEntrado] = useState(false);

  /**
   * EL CIERRE VA POR REFERENCIA, y el efecto corre UNA VEZ.
   *
   * Dependía de `onClose`, que es una flecha nueva en cada render del padre, y
   * `Acontecimientos` re-renderiza cada vez que se mueve la progresión: el
   * efecto se desmontaba y se volvía a montar sin parar, cancelando su propio
   * `requestAnimationFrame` y reiniciando el temporizador. Con la referencia no
   * tiene de qué depender y corre al montar.
   */
  const cerrar = useRef(onClose);
  // La asignación va en su propio efecto y no en el cuerpo: escribir en una
  // `ref` durante el render está prohibido, y con razón — el render tiene que
  // poder repetirse sin efectos secundarios.
  useEffect(() => {
    cerrar.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const a = requestAnimationFrame(() => setEntrado(true));
    return () => cancelAnimationFrame(a);
  }, []);

  /**
   * LA CUENTA ATRÁS EMPIEZA CUANDO LA PIEZA HA ENTRADO, no al montarse.
   *
   * `requestAnimationFrame` NO se ejecuta mientras la pestaña está en segundo
   * plano, pero `setTimeout` sí. Con el temporizador arrancando al montar,
   * subir de nivel con la app de fondo hacía esto: la pieza se montaba, no
   * entraba —sin fotogramas no hay `entrado`—, el temporizador corría igual y
   * se cerraba sola. La subida se daba por enseñada sin que nadie la viera, y
   * como `nivelVisto` ya estaba anotado no volvía a aparecer nunca.
   *
   * Colgando la cuenta atrás de `entrado`, la pieza espera a que haya
   * fotogramas —o sea, a que la estés mirando— y entonces se enseña sus dos
   * segundos y cuatro décimas.
   */
  useEffect(() => {
    if (!entrado) return;
    const t = setTimeout(() => cerrar.current(), DURACION.nivel);
    return () => clearTimeout(t);
  }, [entrado]);

  return (
    <div className={`nivel-up${entrado ? ' nivel-up--dentro' : ''}`} role="status">
      {/* EL DIAL, y no un número dentro de una píldora.
          Es la misma pieza que corona la pantalla de Progreso, y esa repetición
          es el efecto: lo que se enciende al subir es exactamente lo que vas a
          ver la próxima vez que abras Progreso. Un indicador propio para la
          celebración habría dicho «esto es un cartel», no «esto eres tú».

          Los arcos entran a cero y se rellenan solos en el primer fotograma
          —`entrado` los suelta— así que el dial se dibuja CREÁNDOSE. Se anima
          `stroke-dasharray`, que es la excepción que el `Ring` de la app ya usa
          desde el paso 6: no relayouta, se compone. */}
      <Insignia
        size={132}
        nivel={evento.nivel}
        progress={0}
        arcos={evento.arcos.map((a) => ({
          token: a.token,
          progress: entrado ? a.progress : 0,
        }))}
        escala
        label={`Nivel ${evento.nivel}`}
      />
      <span className="stack stack--tight" style={{ alignItems: 'center' }}>
        <span className="t-caption t-dim">Has subido de nivel</span>
        {/* Los dos extremos, no solo el nuevo: «11» dice dónde estás y «7 → 11»
            dice lo que acaba de pasar, que es lo que se está celebrando. */}
        <span className="t-title-3 tnum">
          {evento.desde} → {evento.nivel}
        </span>
      </span>
    </div>
  );
}

function Panel({ evento, onClose }: { evento: Evento; onClose: () => void }): ReactElement {
  // Espera al toque, siempre. Un acontecimiento que se cierra solo mientras lo
  // estás leyendo se siente como una notificación, no como un momento.
  const [listo, setListo] = useState(false);
  // La barra arranca vacía y se rellena en el primer fotograma tras montar. Ese
  // `requestAnimationFrame` no es un tic de más: sin él, React pinta el estado
  // final de una vez y el navegador no tiene un valor anterior desde el que
  // interpolar, así que no hay transición — la barra aparece llena y no se
  // rellena nunca.
  const [lleno, setLleno] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setListo(true), DURACION[evento.kind] * 0.6);
    const a = requestAnimationFrame(() => setLleno(true));
    return () => {
      clearTimeout(t);
      cancelAnimationFrame(a);
    };
  }, [evento]);

  return (
    <div
      className={`evento evento--${evento.kind}`}
      role="dialog"
      aria-modal="true"
      onClick={() => listo && onClose()}
    >
      <div className="evento__panel">
        {evento.kind === 'hito' ? (
          <>
            {/* La misma pieza de siempre, con el mismo rebote de siempre: el
                `rebota` de 1.400 ms que ya vivía aquí es el único rebote visible
                de la app. Lo que cambia es el MATERIAL — ahora es el sello
                acuñado del camino, bisel y cara hundida, para que la pieza que
                celebras sea exactamente la que luego ves en la rampa. */}
            <div className="evento__pieza evento__pieza--sello">
              <span className="evento__cifra tnum">{evento.n}</span>
            </div>
            <p className="t-caption t-dim">Hito alcanzado</p>
            <h2 className="t-display" style={{ margin: 'var(--sp-2) 0' }}>
              {evento.nombre}
            </h2>
            <p className="t-body-strong">{evento.recompensa}</p>

            {/* LA BARRA. Se rellena de un extremo al otro del tramo que acabas
                de cruzar, con los dos números puestos: sin ellos es una barra
                decorativa que siempre llega al final, y con ellos dice de dónde
                vienes. Se anima con `scaleX` y no con `width` porque la ley de
                movimiento del paso 3 es que solo se animan `transform` y
                `opacity`: una es una capa compuesta y la otra relayouta la
                tarjeta entera sesenta veces por segundo. */}
            <div className="subida" aria-hidden="true">
              <div className="subida__pista">
                <div className={`subida__relleno${lleno ? ' subida__relleno--lleno' : ''}`} />
              </div>
              <div className="row row--between">
                <span className="t-caption t-dim tnum">{num(evento.desdePuntos)}</span>
                <span className="t-caption tnum">{num(evento.hastaPuntos)}</span>
              </div>
            </div>
          </>
        ) : null}

        {evento.kind === 'tramo' ? (
          <>
            <div className="evento__pieza" style={{ background: 'transparent', boxShadow: 'none' }}>
              <Divisa tramo={evento.clave} size={72} />
            </div>
            <p className="t-caption">Cruce de tramo</p>
            <h2 className="t-display" style={{ margin: 'var(--sp-2) 0' }}>
              {evento.nombre}
            </h2>
            <p className="t-body-strong">Desde {num(evento.desde)} puntos</p>
          </>
        ) : null}

        {evento.kind === 'temporada' ? (
          <>
            <div style={{ display: 'grid', placeItems: 'center', marginBottom: 'var(--sp-4)' }}>
              <Sello fill={evento.fill} size={128} />
            </div>
            <p className="t-caption t-dim">{evento.etiqueta} · cerrada</p>
            <h2 className="t-display" style={{ margin: 'var(--sp-2) 0' }}>
              {evento.liga}
            </h2>
            <p className="t-body-strong">
              {num(evento.puntos)} puntos · {evento.hitos} {evento.hitos === 1 ? 'hito' : 'hitos'}
            </p>
            <p className="t-sm t-dim" style={{ marginTop: 'var(--sp-4)' }}>
              Guardada en Perfil. La temporada nueva empieza en blanco; el camino, el nivel y las
              rachas siguen donde están.
            </p>
          </>
        ) : null}

        <button
          type="button"
          className="btn btn--block"
          style={{ marginTop: 'var(--sp-6)' }}
          disabled={!listo}
          onClick={onClose}
        >
          Seguir
        </button>
      </div>
    </div>
  );
}

/** Para la pantalla del rewind: recalcular una temporada cerrada sin sellarla. */
export async function recomputeSeason(key: string) {
  const eventos = await collectProgressionEvents();
  const rango = seasonRange(key);
  const dentro = eventos.filter((e) => e.localDate >= rango.from && e.localDate <= rango.to);
  const estado = computeProgression(dentro, { today: rango.to });
  return {
    ...rango,
    points: estado.totalPoints,
    liga: ligaForPoints(estado.totalPoints),
    milestones: MILESTONES.filter((m) => m.points <= estado.totalPoints).length,
  };
}
